import React, { useState } from "react";
import { http, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function LeadQuickAdd({ ref_data, onClose, onCreated }) {
  const [form, setForm] = useState({
    company_name: "",
    country: "",
    industry: "",
    website: "",
    one_line_reason: "",
    source: "",
  });
  const [busy, setBusy] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await http.post("/leads", form);
      toast.success("Lead added");
      onCreated?.(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to add lead");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[560px]" data-testid="quick-add-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Quick-Add Lead</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">Capture in under 30 seconds. Enrichment happens later.</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Company name *</Label>
            <Input required data-testid="qa-company" value={form.company_name} onChange={(e) => update("company_name", e.target.value)} className="mt-1.5" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Country *</Label>
              <Select value={form.country} onValueChange={(v) => update("country", v)}>
                <SelectTrigger className="mt-1.5" data-testid="qa-country"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Primary</div>
                  {ref_data.countries_primary.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-400 font-semibold mt-1">Secondary</div>
                  {ref_data.countries_secondary.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-400 font-semibold mt-1">Support</div>
                  {ref_data.countries_support.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Industry *</Label>
              <Select value={form.industry} onValueChange={(v) => update("industry", v)}>
                <SelectTrigger className="mt-1.5" data-testid="qa-industry"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {ref_data.industries.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Website</Label>
            <Input placeholder="https://…" data-testid="qa-website" value={form.website} onChange={(e) => update("website", e.target.value)} className="mt-1.5" />
          </div>

          <div>
            <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Source * (where found)</Label>
            <Input required placeholder="e.g. LinkedIn, industry directory, referral URL"
                   data-testid="qa-source" value={form.source} onChange={(e) => update("source", e.target.value)} className="mt-1.5" />
          </div>

          <div>
            <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
              Why this looks like a prospect * <span className="text-slate-400 normal-case font-normal">({form.one_line_reason.length}/200)</span>
            </Label>
            <Textarea
              required
              maxLength={200}
              rows={2}
              data-testid="qa-reason"
              value={form.one_line_reason}
              onChange={(e) => update("one_line_reason", e.target.value)}
              className="mt-1.5"
            />
          </div>

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={onClose} data-testid="qa-cancel">Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="qa-submit"
                    className="bg-amber-600 hover:bg-amber-700 text-white">
              {busy ? "Saving…" : "Add Lead"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
